import React, { useEffect, useContext, useState } from 'react';
import dashboardStyles from './Dashboard.style';
import Header from './header/Header';
import { Box, useMediaQuery } from '@material-ui/core';
import {
	getReportsList,
	getSharedReportsList,
	editReport,
	getSavedReportsList,
	dbResetFilter,
	getReportStartAndEndDates
} from './Dashboard.actions';
import { UserPortalContext } from 'context/appcontext';
import { useDispatch, useSelector } from 'react-redux';
import {
	REFRESH_TIME,
	MAX_REFRESH_COUNT,
	PERSONA,
	BASIC,
	SCHEDULED,
	RUNNING,
	NOW
} from 'constants/constant';
import LoadingPage from '../LoadingPage/LoadingPage';
import DashboardMain from './components/DashboardMain';
import {
	getSBUDepartments,
	getCustomAttribute,
	getTempates,
	getCustomDatasetDetails,
	getSavedCustomDataset,
	getCannedReports
} from 'components/Template/Template.actions';
import { trackPageInfo, trackEventInfo } from 'helper/analytics';
import { PAGE_ID, EVENTS, ACTION_ON } from 'constants/tracker';
import { FEATURES } from 'helper/basicFeatureFlag';
import Feedback from './feedback-drawer/FeedbackDrawer';
import { BLOCKED_STATUS_CODE } from 'helper/perimeterX';
import PreviewDialog from 'components/Shared/DialogBox';
import PerimeterXCaptcha from 'components/Shared/PerimeterXCaptcha';
import Banner from './banner/Banner';
import {
	setSelectedList,
	setPrevSelectedList,
	setPrevFilterList,
	setFilterList,
	clearPreview
} from 'components/Preview/Preview.actions';
import activeTab from 'components/Shared/WindowsFocusHandler';

function Dashboard() {
	const dispatch = useDispatch();
	const isSmallScreen = useMediaQuery('(max-width:600px)');
	const portalUserContext = useContext(UserPortalContext);
	const { userDetails, updateUserPersona, customerType, isStudent } = portalUserContext;
	const { companyName, loginId, catManUser, blackOut, sbu, userPersona } = userDetails?.result;
	const classes = dashboardStyles({ isSmallScreen });
	const dashboardError = useSelector((state) => state.dashboardReducer.dashboardErrorCode);
	const { DISABLE_SHARE_WITH_ME_TAB: disableSharedReportApi } = FEATURES[customerType].DASHBOARD;
	// eslint-disable-next-line prettier/prettier
	const { DISABLE_CUSTOM_ATTRIBUTE: disableCustomAttribute, DISABLE_SAVE: disableSave } =
		FEATURES[customerType].PREVIEW_PAGE;
	const { SHOW_CUSTOM_DATASET: showCustomDataset } = FEATURES[customerType].TEMPLATE_PAGE;

	const [refreshCount, setRefreshCount] = useState(1);
	const isTabActive = activeTab();
	const { SUPPLIER, CATMAN, BASIC_VENDOR, BASIC_CATMAN, MERCHANT } = PERSONA;
	const [showCaptchaDialog, setShowCaptchaDialog] = useState(false);
	const reportsList = useSelector((state) => state.dashboardReducer.reportsList);
	window._pxOnCaptchaSuccess = (isValid) => {
		if (isValid) {
			setShowCaptchaDialog(false);
		}
	};

	useEffect(() => {
		let selectedPersona = SUPPLIER;

		if (userPersona === MERCHANT) {
			if (!blackOut) {
				dispatch(getSBUDepartments({ requestBody: loginId, sbu, companyName }));
			}
			selectedPersona = MERCHANT;
		} else {
			/**
			 *  this will help to reset persona when catman change persona to supplier
			 */

			if (catManUser) {
				if (customerType === BASIC) {
					selectedPersona = BASIC_CATMAN;
				} else {
					selectedPersona = CATMAN;
				}
			} else {
				if (customerType === BASIC) {
					selectedPersona = BASIC_VENDOR;
				}
			}
		}
		updateUserPersona(selectedPersona);
		dispatch(editReport({ isEditEnabled: false, editData: {}, action: null }));
		dispatch(dbResetFilter());
		dispatch(getReportsList({ requestBody: loginId, companyName }));
		dispatch(getTempates({ catManUser, companyName, selectedPersona }));
		dispatch(getReportStartAndEndDates({ requestBody: loginId, companyName }));
		if (showCustomDataset) {
			dispatch(getSavedCustomDataset({ catManUser, companyName, selectedPersona }));
			dispatch(getCustomDatasetDetails({ catManUser, companyName, selectedPersona }));
		}
		/** Below dispatch for custom attr and template api */
		if (!disableCustomAttribute) {
			dispatch(getCustomAttribute({ catManUser, companyName, selectedPersona }));
		}
		if (!isStudent) {
			dispatch(getCannedReports({ catManUser, companyName, selectedPersona }));
		}
		if (window.__DATAVENTURES_PORTAL__?.dataLayer?.page?.id !== 'appLaunchPage') {
			trackPageInfo(PAGE_ID['DASHBOARD']);
		}

		if (!disableSave) {
			dispatch(getSavedReportsList({ requestBody: loginId, companyName }));
		}
		dispatch(clearPreview([]));
		dispatch(setSelectedList([]));
		dispatch(setPrevSelectedList([]));
		dispatch(setFilterList([]));
		dispatch(setPrevFilterList([]));
	}, []);

	useEffect(() => {
		if (dashboardError == BLOCKED_STATUS_CODE) {
			// Analytics
			trackEventInfo({
				type: EVENTS.BOT_DETECTION,
				actionOn: ACTION_ON.BOT_DETECTION,
				location: PAGE_ID.DASHBOARD
			});
			setShowCaptchaDialog(true);
		}
	}, [dashboardError]);

	/**
	 * Shared reports Call will happen after a delay according to the customer type (ex: Basic user)
	 * because the right value for disableSharedReportApi flag is set asynchronously
	 */
	useEffect(() => {
		if (!disableSharedReportApi) {
			dispatch(getSharedReportsList({ requestBody: loginId, companyName }));
		}
	}, [disableSharedReportApi]);

	useEffect(() => {
		function refreshReportList() {
			dispatch(getReportsList({ requestBody: loginId, companyName }));
			setRefreshCount((prev) => prev + 1);
		}

		const timer = setTimeout(() => {
			refreshReportList();
		}, REFRESH_TIME);
		// No refresh call for basic users.

		if (customerType === BASIC || !isTabActive) {
			clearTimeout(timer);
		}

		// if no report are in running or schedule status, then no need to make refresh call.
		const isRptRunning = reportsList.some((item) => {
			return (item.status === SCHEDULED && item.frequency === NOW) || item.status === RUNNING;
		});

		/**
		 * Max refresh call count is MAX_REFRESH_COUNT
		 */
		if (!isRptRunning || refreshCount > MAX_REFRESH_COUNT) {
			clearTimeout(timer);
		}

		return () => clearTimeout(timer);
	}, [refreshCount, reportsList, isTabActive]);

	return (
		<Box>
			<PreviewDialog
				open={showCaptchaDialog}
				hidePrimaryBtn={true}
				hideSecondaryBtn={true}
				bodyContent={<PerimeterXCaptcha />}
			/>
			<Header />
			{customerType == BASIC && <Banner />}
			<React.Suspense
				fallback={
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<LoadingPage />
					</Box>
				}>
				<Box className={classes.containerHeight}>
					<DashboardMain />
					{userPersona === MERCHANT && <Feedback />}
				</Box>
			</React.Suspense>
		</Box>
	);
}

Dashboard.propTypes = {};

export default Dashboard;
